import { useEffect, useRef } from 'react';
import api from '../services/api';

const HEARTBEAT_INTERVAL = 20000; // 20 secondes

interface UseHeartbeatProps {
  isAuthenticated: boolean;
}

/**
 * Hook personnalisé pour envoyer un heartbeat automatique au serveur
 * Maintient l'utilisateur comme "en ligne" même sans interaction
 */
export const useHeartbeat = ({ isAuthenticated }: UseHeartbeatProps) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Si l'utilisateur n'est pas authentifié, ne rien faire
    if (!isAuthenticated) {
      if (intervalRef.current) {
        console.log('🔴 HEARTBEAT: Utilisateur déconnecté, arrêt du heartbeat');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    // Fonction pour vérifier et envoyer le heartbeat
    const checkAndSendHeartbeat = () => {
      const token = localStorage.getItem('access_token');
      
      // Si pas de token, arrêter le heartbeat
      if (!token) {
        if (intervalRef.current) {
          console.log('🛑 HEARTBEAT: Token supprimé, arrêt du heartbeat');
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      // Envoyer le heartbeat
      const time = new Date().toLocaleTimeString();
      console.log(`💓 HEARTBEAT: Envoi ping à ${time}...`);
      
      api.post('/api/users/ping/', {})
        .then((response) => {
          console.log(`✅ HEARTBEAT: Réussi à ${time}`, response.data);
        })
        .catch((error) => {
          console.warn(`⚠️ HEARTBEAT: Échec à ${time}`, error);
          // Si 401 (Unauthorized), arrêter le heartbeat
          if (error.response?.status === 401 && intervalRef.current) {
            console.log('🛑 HEARTBEAT: Token invalide, arrêt du heartbeat');
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        });
    };

    const token = localStorage.getItem('access_token');
    
    // Si pas de token initial, ne rien faire
    if (!token) {
      console.log('🔴 HEARTBEAT: Pas de token, heartbeat désactivé');
      return;
    }

    console.log('🟢 HEARTBEAT: Initialisation (intervalle: 20s)');

    // Envoyer un heartbeat immédiatement
    console.log('📤 HEARTBEAT: Premier ping immédiat...');
    checkAndSendHeartbeat();

    // Puis toutes les 20 secondes
    intervalRef.current = setInterval(checkAndSendHeartbeat, HEARTBEAT_INTERVAL);
    console.log('⏱️ HEARTBEAT: Timer démarré (toutes les 20s)');

    // Cleanup : arrêter l'interval quand le composant est démonté
    return () => {
      if (intervalRef.current) {
        console.log('🧹 HEARTBEAT: Cleanup - arrêt du timer');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated]); // Se ré-exécute quand l'état d'authentification change

  return null;
};
